import type { ActionNotice, AsyncOperationEvent, NoticeCategory, TransmissionNotice } from '@core/databus/IMessage'
import type {
  ActionNoticeData,
  AsyncOperationEventData,
  BaseNoticeData,
  NoticeData,
  TransmissionNoticeData,
} from '@core/databus/DataSyncManager'

/**
 * @param noticeList 对应的通知数据的数组
 */
export interface NoticeEventChangeCallback<T> {
  (noticeList: Array<T>): void
}

/**
 * 事件监听回调
 */
export interface INoticeListener<T> {
  // TODO: rename to INoticeEventChangeListener
  /**
   * 新增通知回调
   *
   * {@inheritDoc NoticeEventChangeCallback}
   */
  onAdded: NoticeEventChangeCallback<T>
  /**
   * 更新通知回调
   *
   * {@inheritDoc NoticeEventChangeCallback}
   * @param eventValue 对应的数据数组
   */
  onUpdated: NoticeEventChangeCallback<T>
  /**
   * 删除通知回调
   *
   * {@inheritDoc NoticeEventChangeCallback}
   * @param eventValue 对应的数据数组
   */
  onRemoved: NoticeEventChangeCallback<T>
  /**
   * 初始化回调
   */
  onInitialized: () => void
}

/**
 * 操作状态
 */
export enum NoticeOperationResult {
  FAILED,
  SUCCESS,
  ERROR,
  TIMEOUT,
}

/**
 * 操作返回值结构
 */
export interface INoticeResult<T> {
  /**
   * 操作状态
   *
   * failed:失败 success：成功 error：错误
   */
  status: NoticeOperationResult
  /**
   * 错误信息
   */
  errNotice: string
  /**
   * 具体的返回数据
   */
  data?: T
}

/**
 * 操作回调
 */
export interface NoticeHandlerCallback<T> {
  /**
   * 成功回调
   *
   * @param res {status:success,errNotice:'',data:any}
   */
  onSucceeded(res: INoticeResult<T>): void
  /**
   * 失败回调
   *
   * @param res {status:failed,errNotice:'xxx',data:undefined}
   */
  onFailed(res: INoticeResult<undefined>): void
  /**
   * 错误回调
   *
   * @param res {status:error,errNotice:'xxx',data:undefined}
   */
  onError(res: INoticeResult<undefined>): void
}

/**
 * 实现和端侧的消息的同步机制
 */
export interface IDataSyncManager {
  get webClientId(): string
  // getNoticeById<T>(id: string): Promise<INoticeResult<T>>

  appendActionNotice(notice: ActionNotice): Promise<INoticeResult<ActionNoticeData>>

  appendTransmissionNotice(notice: TransmissionNotice): Promise<INoticeResult<TransmissionNoticeData>>

  appendAsyncOperationEvent(notice: AsyncOperationEvent): Promise<INoticeResult<AsyncOperationEventData>>

  updateNoticeById<T>(id: string, data: Partial<T>): Promise<INoticeResult<BaseNoticeData>>

  getAllNoticesByCategory<T extends BaseNoticeData>(category?: NoticeCategory): Promise<INoticeResult<T[]>>

  addNoticeListener<T extends AsyncOperationEventData | NoticeData | TransmissionNoticeData | ActionNoticeData>(
    category: NoticeCategory,
    listener: INoticeListener<T>
  ): void

  removeNoticeListener<T extends AsyncOperationEventData | NoticeData | TransmissionNoticeData | ActionNoticeData>(
    category: NoticeCategory,
    listener: INoticeListener<T>
  ): boolean

  removeNoticeById(id: string): Promise<INoticeResult<boolean>>

  removeNoticesByIds(ids: string[]): Promise<INoticeResult<boolean>>

  removeNoticeByCategory(category: NoticeCategory): Promise<INoticeResult<boolean>>

  init(): void

  destroy(): void
}
